import React, { useState } from "react";
import {
  Avatar, Box, Menu, MenuItem, Typography, IconButton,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate } from "react-router-dom";

const BRAND = {
  navy:     "#00357a",
  navyDark: "#001f4d",
  gold:     "#c9a96e",
  goldLight:"#dfc08a",
};

function getUser() {
  try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; }
}

export default function NavUserMenu() {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);

  const user = getUser();
  const name  = user?.full_name || user?.name || "";
  const email = user?.email || "";
  const initials = name
    ? name.split(" ").filter(Boolean).map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "U";

  const handleLogout = () => {
    setAnchor(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login", { replace: true });
  };

  return (
    <>
      <IconButton
        onClick={(e) => setAnchor(e.currentTarget)}
        size="small"
        sx={{ p: 0, ml: 0.5 }}
      >
        <Avatar sx={{
          width: 38, height: 38,
          background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldLight})`,
          color: BRAND.navyDark,
          fontSize: 14.5, fontWeight: 800,
          fontFamily: "'Inter', sans-serif",
          border: "2px solid rgba(255,255,255,0.2)",
          transition: "all 0.2s ease",
          cursor: "pointer",
          "&:hover": {
            transform: "scale(1.07)",
            boxShadow: `0 0 0 3px rgba(201,169,110,0.35)`,
            borderColor: BRAND.gold,
          },
        }}>
          {initials}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 0,
          sx: {
            minWidth: 252,
            borderRadius: "16px",
            border: "1px solid rgba(0,53,122,0.1)",
            boxShadow: "0 20px 60px rgba(0,31,77,0.18), 0 4px 12px rgba(0,0,0,0.08)",
            mt: 1.5,
            overflow: "visible",
            background: "#fff",
            "&::before": {
              content: '""', display: "block", position: "absolute",
              top: -1, right: 14, width: 10, height: 10,
              bgcolor: "#fff",
              border: "1px solid rgba(0,53,122,0.1)",
              borderRight: "none", borderBottom: "none",
              transform: "translateY(-50%) rotate(45deg)", zIndex: 0,
            },
          },
        }}
      >
        {/* User identity header */}
        <Box sx={{
          px: 2.5, pt: 2.5, pb: 2,
          display: "flex", alignItems: "center", gap: 1.5,
          background: `linear-gradient(135deg, ${BRAND.navyDark} 0%, ${BRAND.navy} 100%)`,
          borderRadius: "16px 16px 0 0",
          position: "relative", overflow: "hidden",
        }}>
          {/* Subtle background ring */}
          <Box sx={{
            position: "absolute", top: -30, right: -30,
            width: 100, height: 100, borderRadius: "50%",
            border: "1px solid rgba(201,169,110,0.15)",
          }} />
          <Avatar sx={{
            width: 38, height: 38,
            background: `linear-gradient(135deg, rgba(201,169,110,0.25), rgba(201,169,110,0.15))`,
            border: "1px solid rgba(201,169,110,0.4)",
            color: BRAND.gold,
            fontSize: 14.5, fontWeight: 800,
            fontFamily: "'Inter', sans-serif",
          }}>
            {initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontSize: 14, fontWeight: 700,
              fontFamily: "'Playfair Display', serif",
              color: "#fff",
              lineHeight: 1.3,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {name || "User"}
            </Typography>
            <Typography sx={{
              fontSize: 12.5, color: "rgba(255,255,255,0.45)",
              fontFamily: "'Inter', sans-serif",
              mt: 0.2,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {email}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ px: 1.5, py: 1.5 }}>
          {/* 2026-10-05 — "Configure Lab" moved out of here entirely, per
              explicit instruction: lab/report-type configuration is an
              administrative capability, not something every regular staff
              member should self-serve from their own account menu. It now
              lives in AdminDashboard.js, where /api/config/labs's write
              endpoints are now actually gated (get_current_admin) — this
              menu item used to call them with NO auth header at all,
              which is the real reason it always failed with "failed to
              load labs" regardless of who clicked it. */}
          {/* Sign out */}
          <MenuItem
            onClick={handleLogout}
            sx={{
              gap: 1.5, py: 1.1, px: 1.5, borderRadius: "10px",
              color: "#dc2626", fontSize: 15, fontWeight: 600,
              fontFamily: "'Inter', sans-serif",
              transition: "all 0.15s",
              "&:hover": { bgcolor: "rgba(220,38,38,0.06)" },
            }}
          >
            <Box sx={{
              width: 30, height: 30, borderRadius: "8px",
              display: "flex", alignItems: "center", justifyContent: "center",
              bgcolor: "rgba(220,38,38,0.07)",
              flexShrink: 0,
            }}>
              <LogoutIcon sx={{ fontSize: 16, color: "#dc2626" }} />
            </Box>
            Sign Out
          </MenuItem>
        </Box>

        {/* Footer badge */}
        <Box sx={{
          mx: 1.5, mb: 1.5, px: 2, py: 1,
          borderRadius: "8px",
          background: `linear-gradient(135deg, rgba(201,169,110,0.08), rgba(201,169,110,0.04))`,
          border: "1px solid rgba(201,169,110,0.2)",
          display: "flex", alignItems: "center", gap: 1,
        }}>
          <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: BRAND.gold, flexShrink: 0,
            "@keyframes navPulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.3 } },
            animation: "navPulse 2s infinite",
          }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND.gold, letterSpacing: "0.1em", fontFamily: "'Inter', sans-serif" }}>
            LABARIUM INTELLIGENCE — ACTIVE
          </Typography>
        </Box>
      </Menu>
    </>
  );
}
